import React from 'react';
import { Table, Badge } from 'react-bootstrap';
import '../components/PlayerComponents.css';
import { PLAYER_ASSETS_ENABLED } from '../constants/featureFlags';



type PlayerAssetType = 'qrCode' | 'playerIdCard';

interface Player {
  _id: string;
  playerId?: string;
  fullName: string;
  team: {
    name: string;
    shortName: string;
    logo: string;
  };
  dob: string;
  sports: string;
  verifyStat: 'Approved' | 'Referred Back' | 'Pending';
  paymentStat: 'Paid' | 'Not Paid' | 'Pending';
  amountPaid: number;
  amountDue: number;
  playerImage: string;
  ageCategory: string;
  isSelected?: boolean;
  qrCodeUrl?: string;
  playerIdCardUrl?: string;
}

interface PlayerTableProps {
  players: Player[];
  loading?: boolean;
  showCheckboxes?: boolean;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showPaymentColumns?: boolean; // New prop to control payment columns visibility
  allowActionsForApprovedPlayers?: boolean; // New prop to control whether approved players can be edited/deleted
  showQrActions?: boolean;
  showPlayerIdCardActions?: boolean;
  assetColumnMode?: 'separate' | 'combined';
  onAssetView?: (player: Player, assetType: PlayerAssetType) => void;
  onAssetDownload?: (player: Player, assetType: PlayerAssetType) => void;
  onPlayerSelect?: (playerId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  onView?: (player: Player) => void;
  onEdit?: (player: Player) => void;
  onDelete?: (player: Player) => void;
  title?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
}

const PlayerTable: React.FC<PlayerTableProps> = ({
  players,
  loading = false,
  showCheckboxes = false,
  showView = true,
  showEdit = true,
  showDelete = true,
  showPaymentColumns = true, // Default to true for backward compatibility
  allowActionsForApprovedPlayers = false, // Default to false for backward compatibility
  showQrActions = false,
  showPlayerIdCardActions = false,
  assetColumnMode = 'separate',
  onAssetView,
  onAssetDownload,
  onPlayerSelect,
  onSelectAll,
  onView,
  onEdit,
  onDelete,
  title = 'Player Lineups',
  emptyMessage = 'No players found',
  emptySubMessage = 'No players available. Add a new player to get started.'
}) => {
  const assetsEnabled = PLAYER_ASSETS_ENABLED;
  const resolvedShowQrActions = assetsEnabled && showQrActions;
  const resolvedShowPlayerIdCardActions = assetsEnabled && showPlayerIdCardActions;
  const hasAssetColumns = resolvedShowQrActions || resolvedShowPlayerIdCardActions;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="empty-state">
          <i className="ri-user-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
          <h5 className="mt-3">{emptyMessage}</h5>
          <p className="text-muted">{emptySubMessage}</p>
        </div>
      </div>
    );
  }

  const allSelected = players.every(player => player.isSelected);
  const someSelected = players.some(player => player.isSelected);

  const renderAssetActions = (player: Player, assetType: PlayerAssetType) => {
    const assetUrl = assetType === 'qrCode' ? player.qrCodeUrl : player.playerIdCardUrl;
    
    // For player ID card, only show actions if player is verified (Approved)
    // For QR code, show actions if URL exists (existing behavior)
    const isDisabled = assetType === 'playerIdCard' 
      ? !assetUrl || player.verifyStat !== 'Approved'
      : !assetUrl;

    return (
      <div className="asset-action-wrapper">
        <button
          type="button"
          className="asset-btn asset-btn-view"
          disabled={isDisabled}
          onClick={() => !isDisabled && onAssetView?.(player, assetType)}
          title="View"
        >
          <i className="ri-eye-line"></i>
        </button>
        <button
          type="button"
          className="asset-btn asset-btn-download"
          disabled={isDisabled}
          onClick={() => !isDisabled && onAssetDownload?.(player, assetType)}
          title="Download"
        >
          <i className="ri-download-line"></i>
        </button>
      </div>
    );
  };

  const renderCombinedAssetCell = (player: Player) => (
    <td>
      <div className="combined-asset-column">
        {resolvedShowQrActions && (
          <div className="combined-asset-row">
            <span className="asset-label">QR Code</span>
            {renderAssetActions(player, 'qrCode')}
          </div>
        )}
        {resolvedShowPlayerIdCardActions && (
          <div className="combined-asset-row">
            <span className="asset-label">ID Card</span>
            {renderAssetActions(player, 'playerIdCard')}
          </div>
        )}
      </div>
    </td>
  );

  return (
    <div className="player-table-container">
      <div className="player-table-responsive">
        <Table className="teams-table">
          <thead>
            <tr>
              {showCheckboxes && (
                <th>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                  />
                </th>
              )}
              <th>Profile</th>
              <th>Player Name</th>
              <th>Team Name</th>
              <th>DOB</th>
              <th>Sports</th>
                              <th>Verify Status</th>
                              {showPaymentColumns && <th>Payment Status</th>}
              {showPaymentColumns && <th>Amount Paid</th>}
              {showPaymentColumns && <th>Amount Due</th>}
              {assetColumnMode === 'combined' && hasAssetColumns ? (
                <th className="asset-column-header">Player Documents</th>
              ) : (
                <>
                  {resolvedShowQrActions && <th className="asset-column-header qr-code-column">QR Code</th>}
                  {resolvedShowPlayerIdCardActions && <th className="asset-column-header player-id-card-column">Player ID Card</th>}
                </>
              )}
              {(showEdit || showView || showDelete) && <th className="actions-column-header">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {players.map((player) => (
              <tr key={player._id}>
                {showCheckboxes && (
                  <td>
                    <input
                      type="checkbox"
                      checked={player.isSelected || false}
                      onChange={(e) => onPlayerSelect?.(player._id, e.target.checked)}
                      disabled={player.paymentStat === 'Paid'}
                    />
                  </td>
                )}
                <td>
                  <div className="player-profile-small">
                    {player.playerImage ? (
                      <img 
                        src={player.playerImage}
                        alt={player.fullName}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('d-none');
                        }}
                      />
                    ) : null}
                    {!player.playerImage && (
                      <div className="default-profile-icon-small">
                        <i className="ri-user-line"></i>
                      </div>
                    )}
                  </div>
                </td>
                <td>{player.fullName}</td>
                <td>
                  <span className="team-name">{player.team.name}</span>
                </td>
                <td>{player.dob}</td>
                <td>{player.sports}</td>
                <td>
                  <Badge 
                    bg={player.verifyStat === 'Approved' ? 'success' : player.verifyStat === 'Referred Back' ? 'warning' : 'warning'}
                    className="verify-badge"
                  >
                    {player.verifyStat}
                  </Badge>
                </td>
                {showPaymentColumns && (
                  <td>
                    <Badge 
                      bg={player.paymentStat === 'Paid' ? 'success' : 'danger'}
                      className="payment-badge"
                    >
                      {player.paymentStat}
                    </Badge>
                  </td>
                )}
                {showPaymentColumns && <td>₹{player.amountPaid}</td>}
                {showPaymentColumns && <td>₹{player.amountDue}</td>}
                {assetColumnMode === 'combined' && hasAssetColumns && renderCombinedAssetCell(player)}
                {assetColumnMode === 'separate' && resolvedShowQrActions && (
                  <td className="asset-column-cell qr-code-column">{renderAssetActions(player, 'qrCode')}</td>
                )}
                {assetColumnMode === 'separate' && resolvedShowPlayerIdCardActions && (
                  <td className="asset-column-cell player-id-card-column">{renderAssetActions(player, 'playerIdCard')}</td>
                )}
                {(showEdit || showView || showDelete) && (
                  <td>
                    <div className="action-buttons">
                      {showView && (
                        <button
                          onClick={() => onView?.(player)}
                          className="btn-view"
                          title="View Details"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                      )}
                      {showEdit && (
                        <button
                          onClick={() => onEdit?.(player)}
                          className="btn-edit"
                          title="Edit"
                          disabled={player.verifyStat === 'Approved' && !allowActionsForApprovedPlayers}
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                      )}
                      {showDelete && (
                        <button
                          onClick={() => onDelete?.(player)}
                          className="btn-delete"
                          title="Delete"
                          disabled={player.verifyStat === 'Approved' && !allowActionsForApprovedPlayers}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default PlayerTable; 